import { FormEvent, useMemo, useState } from 'react';
import { RegistrationContainer } from '@bumble/app/components/RegistrationContainer';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

type AccountChoice = { account_domain: string; account_id: string };
type AccountOption = { label: string; value: string };

const NONE_VALUE = '__NONE__';

export const ForgotPassword = () => {
  const api = useApi();
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [choices, setChoices] = useState<AccountChoice[] | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>(NONE_VALUE);

  const accountOptions: AccountOption[] = useMemo(
    () =>
      Array.isArray(choices)
        ? choices.map((c) => ({ label: c.account_domain, value: String(c.account_id) }))
        : [],
    [choices]
  );

  const handleAccountChange = (e: SelectChangeEvent) => {
    setSelectedAccountId(e.target.value);
  };

  const forgotPassword = useMutation(api.forgotPasswordApiV1ForgotPasswordPost, {
    onSuccess() {
      toast.success('Email sent.');
      setSuccess(true);
    },
    onError: (err: any) => {
      if (err?.status === 300) {
        const detail = err?.error?.detail ?? err?.response?.data?.detail;
        let parsed: unknown = detail;
        if (typeof detail === 'string') parsed = JSON.parse(detail);

        if (Array.isArray(parsed)) {
          setChoices(parsed as AccountChoice[]);
          setSelectedAccountId((prev) => (prev !== NONE_VALUE ? prev : NONE_VALUE));
          toast.info('Select an account and click "Send Email" again.');
          return;
        }
      }
      apiError(err);
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload: { email: string; account_id?: string } = { email };
    if (selectedAccountId !== NONE_VALUE) payload.account_id = selectedAccountId;
    forgotPassword.mutate(payload);
  };

  if (success) {
    return (
      <RegistrationContainer>
        <Typography variant="h3" component="h1" mb={2}>
          Forgot password?
        </Typography>
        <Alert severity="success" sx={{ textAlign: 'left', my: 5 }}>
          An email confirmation has been sent to your registered email address.
        </Alert>
        <Box py={6}>
          <Divider />
        </Box>
        <Button size="small" variant="text" component={Link} to="/login">
          Already have an account?
        </Button>
      </RegistrationContainer>
    );
  }
  return (
    <RegistrationContainer>
      <Typography variant="h3" component="h1" mb={2}>
        Forgot password?
      </Typography>
      <Typography variant="body1" component="p" py={2} mb={2}>
        Enter your email address below and we&apos;ll send you a password reset.
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email
          Address"
          margin="normal"
          type="text"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setEmail(e.target.value)}
        />
        {Array.isArray(accountOptions) && accountOptions.length > 0 &&  <Box mt={2}>
          <FormControl fullWidth>
            <InputLabel id="account-select-label">Account</InputLabel>
            <Select
              labelId="account-select-label"
              label="Account"
              value={selectedAccountId}
              onChange={handleAccountChange}
              displayEmpty
              renderValue={(val) => {
                if (val === NONE_VALUE) return 'None';
                const opt = accountOptions.find((o) => o.value === val);
                return opt?.label ?? 'None';
              }}
            >
              <MenuItem value={NONE_VALUE}>None</MenuItem>
              {accountOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>}
        <Box mt={4}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            type="submit"
            disabled={email.length < 5 || forgotPassword.isLoading}
          >
            Send Email
          </Button>
        </Box>
      </Box>
      <Box py={6}>
        <Divider />
      </Box>
      <Button size="small" variant="text" component={Link} to="/login">
        Already have an account?
      </Button>
    </RegistrationContainer>
  );
};
